import { useState } from "react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { useTranslation } from "@repo/core";
import { StockListItem } from "./StockListItem";

interface Stock {
  id: string;
  ticker: string;
  companyName: string;
  logo: string;
  percentage: number;
  isPositive: boolean;
}

interface LatestConstituentsListProps {
  longStocks: Stock[];
  shortStocks: Stock[];
  stockDetails: { [key: string]: { name: string; logo: string } };
  portfolioConstruction?: "LongOnly" | "ShortOnly" | "LongShort";
  initialVisibleCount?: number;
}

/**
 * Displays the latest portfolio constituents (Long/Short) with expandable lists
 */
export const LatestConstituentsList = ({
  longStocks,
  shortStocks,
  stockDetails,
  portfolioConstruction = "LongShort",
}: LatestConstituentsListProps) => {
  const { tStrategy, tCommon } = useTranslation();
  const [isLongExpanded, setIsLongExpanded] = useState(false);
  const [isShortExpanded, setIsShortExpanded] = useState(false);

  const showLongStocks = portfolioConstruction !== "ShortOnly" && longStocks.length > 0;
  const showShortStocks = portfolioConstruction !== "LongOnly" && shortStocks.length > 0;

  if (!showLongStocks && !showShortStocks) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Long Stocks Section */}
      {showLongStocks && (
        <div>
          <button
            onClick={() => setIsLongExpanded(!isLongExpanded)}
            className="w-full flex items-center justify-between py-2 text-left hover:bg-gray-50 rounded-lg px-2 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            <div className="flex items-center gap-2">
              {isLongExpanded ? (
                <HiChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <HiChevronDown className="w-5 h-5 text-gray-500" />
              )}
              <span className="text-sm font-semibold text-gray-700">
                {tStrategy("longStocks")} ({longStocks.length} {longStocks.length === 1 ? tCommon("stock") : tCommon("stocks")})
              </span>
            </div>
          </button>

          {isLongExpanded && (
            <div className="mt-3 flex flex-wrap gap-2">
              {longStocks.map((stock) => (
                <div key={stock.id} className="w-full sm:w-[calc(33.333%-0.5rem)] lg:w-[calc(25%-0.5rem)] xl:w-[calc(20%-0.5rem)]">
                  <StockListItem
                    ticker={stock.ticker}
                    companyName={stock.companyName}
                    percentage={stock.percentage}
                    isPositive={stock.isPositive}
                    stockDetails={stockDetails}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Short Stocks Section */}
      {showShortStocks && (
        <div>
          <button
            onClick={() => setIsShortExpanded(!isShortExpanded)}
            className="w-full flex items-center justify-between py-2 text-left hover:bg-gray-50 rounded-lg px-2 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            <div className="flex items-center gap-2">
              {isShortExpanded ? (
                <HiChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <HiChevronDown className="w-5 h-5 text-gray-500" />
              )}
              <span className="text-sm font-semibold text-gray-700">
                {tStrategy("shortStocks")} ({shortStocks.length} {shortStocks.length === 1 ? tCommon("stock") : tCommon("stocks")})
              </span>
            </div>
          </button>

          {isShortExpanded && (
            <div className="mt-3 flex flex-wrap gap-2">
              {shortStocks.map((stock) => (
                <div key={stock.id} className="w-full sm:w-[calc(33.333%-0.5rem)] lg:w-[calc(25%-0.5rem)] xl:w-[calc(20%-0.5rem)]">
                  <StockListItem
                    ticker={stock.ticker}
                    companyName={stock.companyName}
                    percentage={stock.percentage}
                    isPositive={stock.isPositive}
                    stockDetails={stockDetails}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LatestConstituentsList;
